import React, { useEffect, useRef } from 'react';
import CompanyLogo, { CoBrandDivider } from './CompanyLogo';
import { OnHandLogo, SyncIndicator } from './brand/OnHandLogo';
import UserMenu from './UserMenu';
import { Avatar, NavLinks, PageTabs, splitMenu } from './AppNav';
import { usePageBarSlot } from './PageBar';
import useMediaQuery from '../hooks/useMediaQuery';

/*
  THE TOP BAR, rebrand step 4, 17 September 2026 (brand guide 11.7 and 12.2).

  **The tiers came back on 3 October 2026**, the owner's decision: the rebrand
  had split one floating object into three stacked strips, and his word for the
  result was "very disjointed". He asked for the tiered bar back with the new
  design language on it, not for the rebrand undone.

  One glass panel, 18 corner, floating 14 from the edges of the screen, built of
  tiers. **A tier with nothing to show renders nothing**, so a screen with one
  page and no controls still gets a plain 60 tall bar:

    top        the OnHand logo 118 wide (104 on a phone), the company's logo,
               the areas as links, the syncing mark and your initials
    pages      the pages of the area you are in, when it has more than one
    controls   whatever the screen hands up through `PageBar`

  **Tier 3 is the slot and offering it is the whole trick.** 73 places in the app
  already hand their controls up through `PageBar`, and they had been drawing
  them in the page only because the rebrand's bar offered nowhere to put them.
  Nothing about any page's state moved, then or now.

  **Below 1024 is untouched**, his instruction: the slim bar and the dock stay
  exactly as the rebrand left them.

  **Every link is the same link.** The areas come from the one menu
  (src/lib/menu.jsx), filtered by job level exactly as before, and pressing one
  opens the first page of that area, as it always has. **Settings is the one
  area that moved**: guide 11.7 puts it in the avatar menu for every job level,
  so its pages are listed there, with the same pages and the same visibility.

  Three shapes, all this one component:

    desk (1024 and up)   links, then your initials opening the menu with your
                         Settings pages, your sites and Sign Out (UserMenu)
    held, 600 to 1023    the same links; your initials open the sheet from the
                         bottom that a phone has, because below 1024 OnHand has
                         never offered the site switcher and still does not
                         (known issue 26 in docs/audit/RULES_AND_KNOWN_ISSUES.md)
    held, under 600      logo, sync and initials only; the areas are in the tab
                         bar at the bottom (TabBar in AppNav.jsx)

  `context` replaces the links with a screen's own heading, for count entry,
  which has no areas of its own. `right` replaces the initials.
*/

// The syncing word fits beside the initials from here; below it the mark alone
// shows and the word is kept for a screen reader (rebrand step 2, measured).
const SYNC_WORD_AT = '(min-width: 1280px)';

export default function AppHeader({
  areas = [], activeKey, onSelect, area,
  userName, roleLabel, onSignOut,
  held = false, onAccount, accountOpen = false,
  context, right,
}) {
  const barRef = useRef(null);
  const showWord = useMediaQuery(SYNC_WORD_AT);
  const phone = useMediaQuery('(max-width: 599px)');
  const { setSlot } = usePageBarSlot();
  /* Settings is an area again on a desk, and stays in the account sheet below
     1024. See the note on `splitMenu`. */
  const { main, settings } = splitMenu(areas, { keepSettings: !held });
  const inSettings = !!settings && settings.pages.some(p => p.key === activeKey);

  /*
    The pop-up messages sit 24 under this bar, never over it (rebrand step 3,
    guide 10.4), and on a desk the bar's right edge clears the scrollbar of the
    area that scrolls under it, so it lines up with the page rather than
    overhanging the scrollbar.
  */
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return undefined;
    const shell = bar.closest('.app-shell');
    const content = shell && shell.querySelector('.content-area');
    let lastSb = null;
    let lastTop = null;
    const sync = () => {
      const top = bar.getBoundingClientRect().bottom + 24;
      if (top !== lastTop) {
        lastTop = top;
        document.documentElement.style.setProperty('--oh-toast-top', top + 'px');
        /* **The page starts below the bar, and the bar is no longer one
           height.** It was 98 hard-coded in the stylesheet, which is 14 above
           it plus 60 of bar plus the app's 24 gap. With tiers the bar grows and
           shrinks per screen, so the page is told where it actually ends.
           Measured rather than computed from the tiers, because a wrapped row
           of pills is taller than a tier is nominally worth. */
        document.documentElement.style.setProperty('--oh-bar-bottom', top + 'px');
      }
      if (content) {
        const sb = Math.max(0, content.offsetWidth - content.clientWidth);
        if (sb !== lastSb) { lastSb = sb; shell.style.setProperty('--dash-sb', sb + 'px'); }
      }
    };
    sync();
    /*
      **The writes happen on the next frame, not inside the observer.**

      Measuring the bar and setting the page's top padding from the same
      ResizeObserver callback is a loop: the padding changes, the content area
      resizes, the observer is watching the content area, and it fires again.
      The browser reports that as "ResizeObserver loop completed with undelivered
      notifications" and it filled the screen with errors the moment the tiers
      went in, on 3 October 2026.

      The equality guards above settle it after one extra pass, but the warning
      is still correct: work that changes layout does not belong inside the
      callback that observes layout. One frame later it does.
    */
    let frame = null;
    const later = () => {
      if (frame != null) return;
      frame = requestAnimationFrame(() => { frame = null; sync(); });
    };
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(later) : null;
    if (ro) { ro.observe(bar); if (content) ro.observe(content); }
    window.addEventListener('resize', later);
    return () => {
      if (frame != null) cancelAnimationFrame(frame);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', later);
      document.documentElement.style.removeProperty('--oh-toast-top');
      document.documentElement.style.removeProperty('--oh-bar-bottom');
    };
  }, []);

  let account;
  if (right !== undefined) account = right;
  else if (held) {
    account = (
      <button type="button" className={'oh-avatar-btn' + (inSettings ? ' on' : '')}
        onClick={onAccount} aria-haspopup="dialog" aria-expanded={accountOpen}
        aria-label={'Your account and settings' + (userName ? ', ' + userName : '')}>
        <Avatar name={userName} size={phone ? 36 : 40} />
      </button>
    );
  } else {
    /*
      **On a desk the menu is you, your sites and Sign out, and nothing else**,
      the owner's instruction of 3 October 2026: *"it should only be the site
      picker, user details and sign out."*

      Settings is an area again, so listing its pages here as well was the same
      menu in two places. It is not lit either, for the same reason: the Settings
      link in the bar lights when you are in it, and two things saying so is one
      too many.

      **Below 1024 the sheet still carries Settings** and must, because there it
      is the only way in.
    */
    account = (
      <UserMenu userName={userName} roleLabel={roleLabel} onSignOut={onSignOut}
        settings={null} activeKey={activeKey} onSelect={onSelect} lit={false} />
    );
  }

  /*
    **Tier 2 and tier 3, on a desk only.** A tier with nothing to show renders
    nothing, so the bar is 60 tall on a screen with one page and no controls,
    and grows only where a screen has more to say.

    Tier 3 is the slot `PageBar` portals into. Offering it is the whole of what
    brings a page's search, filters and buttons back up into the bar: 73 places
    in the app already hand their controls up through `PageBar`, and they have
    been drawing them in the page only because this bar stopped offering
    anywhere to put them.
  */
  const tiers = !held && !context;

  return (
    <header className={'oh-top' + (held ? ' is-held' : ' is-desk')}>
      <div ref={barRef} className={'oh-topbar' + (tiers ? ' has-tiers' : '')}>
        <div className="oh-tier oh-tier-top">
          <div className="oh-topbar-brand">
            {/*
              **160 wide on a desk**, the owner's instruction of 3 October 2026.
              It was 118, which is what the brand guide's 11.7 gives, and it
              looked small to him. It was: 118 wide comes out 20 tall in a 60
              tall row, leaving 40 of air, and it was smaller than the company's
              own logo standing next to it at 24.

              160 comes out about 27 tall, so it sits a little above the company
              logo rather than under it, with 33 still clear in the row.
              **A departure from 11.7, recorded in DECISIONS.md.**

              **The phone stays at 104**, the guide's 12.2: that bar is 56 tall
              and much narrower, and he asked for below 1024 to be left alone.

              `iconSize` sets the lockup's width; these two give exactly those.
            */}
            <OnHandLogo iconSize={(phone ? 104 : 160) * 101 / 386.58} animate="launch" />
            <span className="oh-topbar-co">
              <CoBrandDivider height={24} />
              <CompanyLogo height={24} maxWidth={phone ? 88 : 132} fallbackToName={false} />
            </span>
          </div>
          {context
            ? <div className="oh-topbar-context">{context}</div>
            : <NavLinks areas={main} activeKey={activeKey} onSelect={onSelect} />}
          <div className="oh-topbar-right">
            <SyncIndicator showWord={showWord && !held} />
            {account}
          </div>
        </div>
        {tiers && area && area.pages && area.pages.length > 1 && (
          <div className="oh-tier oh-tier-pages">
            <PageTabs area={area} activeKey={activeKey} onSelect={onSelect} />
          </div>
        )}
        {tiers && <div ref={setSlot} className="oh-tier oh-tier-controls" />}
      </div>
    </header>
  );
}
